import type { ReactNode } from 'react';
import type { ToastOptions } from 'react-toastify';

import { toast } from 'react-toastify';

export const TOAST_TYPE = {
  DEFAULT: 'default',
  ERROR: 'error',
  INFO: 'info',
  SUCCESS: 'success',
  WARNING: 'warning',
} as const;

type TypeOptions = (typeof TOAST_TYPE)[keyof typeof TOAST_TYPE];

export const showToast = ({
  message = '',
  type = TOAST_TYPE.SUCCESS,
  autoClose = 4000,
}: {
  message?: ReactNode;
  type?: TypeOptions;
  autoClose?: number | false;
}) => {
  if (document.hidden) return;

  if (type === TOAST_TYPE.ERROR) {
    autoClose = autoClose ?? 10000;
  } else if (type === TOAST_TYPE.INFO) {
    autoClose = autoClose ?? 2000;
  }

  const toastOptions: ToastOptions<TypeOptions> = {
    position: 'top-right',
    autoClose,
    closeOnClick: true,
    pauseOnHover: true,
    pauseOnFocusLoss: false,
  };

  if (type === TOAST_TYPE.DEFAULT) {
    toast(message, toastOptions);
    return;
  }

  toast[type](message, toastOptions);
};

export const showErrorToast = (err: any) => {
  const maxMsgLength = 256;

  if (!('response' in err || 'message' in err)) return;

  if (Array.isArray(err.response?.data)) {
    return err.response.data.forEach((errObj: any) => {
      if (typeof errObj.msg === 'string' && errObj.msg.length < maxMsgLength) {
        showToast({ message: errObj.msg, type: TOAST_TYPE.ERROR });
      }
    });
  }

  if (typeof err.response?.data === 'string' && err.response.data.length < maxMsgLength) {
    return showToast({ message: err.response.data, type: TOAST_TYPE.ERROR });
  }

  if (
    typeof err.response?.data?.message === 'string' &&
    err.response?.data?.message.length < maxMsgLength
  ) {
    return showToast({ message: err.response.data.message, type: TOAST_TYPE.ERROR });
  }

  if (typeof err.message === 'string' && err.message.length < maxMsgLength) {
    return showToast({ message: err.message, type: TOAST_TYPE.ERROR });
  }

  showToast({
    message: 'Something went wrong. Please contact support if the problem persists.',
    type: TOAST_TYPE.ERROR,
  });
};

export const showSuccessToast = (message: ReactNode) => {
  showToast({ message, type: TOAST_TYPE.SUCCESS });
};
